import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, TileLayer } from "react-leaflet";
import { useParams } from "react-router-dom";
import L from "leaflet";
import markerIconUrl from "leaflet/dist/images/marker-icon.png";
import markerShadowUrl from "leaflet/dist/images/marker-shadow.png";

import LocationPicker from "../components/LocationPicker";
import SuccessScreen from "../components/SuccessScreen";
import { getCarInfo, requestDelivery } from "../services/api";

const previewIcon = new L.Icon({
  iconUrl: markerIconUrl,
  shadowUrl: markerShadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const STATUS_META = {
  parked: {
    label: "Park Halinde",
    badgeClass: "badge-primary",
  },
  delivery_requested: {
    label: "Teslim Bekleniyor",
    badgeClass: "badge-warning",
    title: "Teslim Alma İsteğiniz Alındı",
    description: "Valeniz aracınızı hazırlıyor...",
    iconClass: "status-icon-warning",
    icon: "!",
  },
  delivering: {
    label: "Teslim Ediliyor",
    badgeClass: "badge-info",
    title: "Aracınız Yolda!",
    description: "Valeniz aracınızı getiriyor...",
    iconClass: "status-icon-info",
    icon: "→",
  },
  delivered: {
    label: "Teslim Edildi",
    badgeClass: "badge-success",
    title: "Araç Teslim Edildi",
    description: "İyi yolculuklar!",
    iconClass: "status-icon-success",
    icon: "✓",
  },
};

function CustomerDelivery() {
  const { qrCode } = useParams();
  const [carInfo, setCarInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [deliverySuccess, setDeliverySuccess] = useState(false);
  const [formData, setFormData] = useState({
    customerName: "",
    customerPhone: "",
    noteToValet: "",
    pickupLocation: null,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchCarInfo = async () => {
      setIsLoading(true);
      setErrorMessage("");
      setAlertMessage("");
      setDeliverySuccess(false);

      try {
        const response = await getCarInfo(qrCode);

        if (!isMounted) {
          return;
        }

        setCarInfo(response.data);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(error.message);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchCarInfo();

    return () => {
      isMounted = false;
    };
  }, [qrCode]);

  const statusMeta = useMemo(() => {
    if (!carInfo?.status) {
      return STATUS_META.parked;
    }

    return STATUS_META[carInfo.status] || STATUS_META.parked;
  }, [carInfo]);

  const isFormValid = formData.customerName.trim().length > 0;

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleLocationConfirm = (pickupLocation) => {
    setFormData((current) => ({
      ...current,
      pickupLocation,
    }));
    setShowLocationPicker(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isFormValid) {
      setAlertMessage("Lütfen adınızı soyadınızı girin.");
      return;
    }

    setIsSubmitting(true);
    setAlertMessage("");

    try {
      const payload = {
        customerName: formData.customerName.trim(),
        customerPhone: formData.customerPhone.trim(),
        pickupLocation: formData.pickupLocation,
        noteToValet: formData.noteToValet.trim(),
      };

      const response = await requestDelivery(qrCode, payload);

      setDeliverySuccess(true);
      setCarInfo((current) => ({
        ...(current || {}),
        plate: response.data?.plate || current?.plate,
        carModel: response.data?.carModel || current?.carModel,
        status: "delivery_requested",
      }));
    } catch (error) {
      setAlertMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStatusScreen = () => (
    <section className="card status-card">
      <div className={`status-icon ${statusMeta.iconClass}`}>{statusMeta.icon}</div>
      <h2>{statusMeta.title}</h2>
      <p className="status-copy">{statusMeta.description}</p>
    </section>
  );

  if (isLoading) {
    return (
      <main className="customer-layout">
        <div className="brand-block">
          <h1>QR Vale</h1>
          <div className="brand-divider" />
        </div>

        <section className="card centered-card">
          <div className="spinner" />
          <p>Araç bilgileriniz yükleniyor...</p>
        </section>
      </main>
    );
  }

  if (errorMessage) {
    return (
      <main className="customer-layout">
        <div className="brand-block">
          <h1>QR Vale</h1>
          <div className="brand-divider" />
        </div>

        <section className="card centered-card error-card">
          <div className="status-icon status-icon-danger">!</div>
          <h2>İşlem Gerçekleştirilemedi</h2>
          <p className="status-copy">{errorMessage}</p>
          <p className="status-copy">QR Kodu: {qrCode}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="customer-layout">
      <div className="brand-block">
        <h1>QR Vale</h1>
        <div className="brand-divider" />
      </div>

      <section className="card vehicle-card">
        <div className="vehicle-card-top">
          <div>
            <span className="summary-label">Plaka</span>
            <h2>{carInfo?.plate}</h2>
          </div>
          <span className={`status-badge ${statusMeta.badgeClass}`}>
            {statusMeta.label}
          </span>
        </div>
        <p className="vehicle-model">{carInfo?.carModel}</p>
      </section>

      {alertMessage ? <div className="page-alert">{alertMessage}</div> : null}

      {deliverySuccess ? (
        <SuccessScreen plate={carInfo?.plate} carModel={carInfo?.carModel} />
      ) : carInfo?.status === "parked" ? (
        <section className="card form-card">
          <div className="section-head">
            <h2>Teslim Alma Formu</h2>
            <p>Formu doldurarak aracınızın size getirilmesini isteyebilirsiniz.</p>
          </div>

          <form onSubmit={handleSubmit} className="delivery-form">
            <label className="field">
              <span>Adınız Soyadınız</span>
              <input
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleInputChange}
                placeholder="Ad Soyad"
                required
              />
            </label>

            <label className="field">
              <span>Telefon Numaranız</span>
              <input
                type="tel"
                name="customerPhone"
                value={formData.customerPhone}
                onChange={handleInputChange}
                placeholder="05XX XXX XX XX"
              />
            </label>

            <div className="field">
              <span>Teslim Alma Konumu</span>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setShowLocationPicker((current) => !current)}
              >
                {showLocationPicker ? "Haritayı Kapat" : "Konum Ekle"}
              </button>
            </div>

            {formData.pickupLocation ? (
              <section className="mini-map-card">
                <div className="mini-map-shell">
                  <MapContainer
                    center={[
                      formData.pickupLocation.lat,
                      formData.pickupLocation.lng,
                    ]}
                    zoom={15}
                    scrollWheelZoom={false}
                    dragging={false}
                    doubleClickZoom={false}
                    touchZoom={false}
                    zoomControl={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker
                      position={[
                        formData.pickupLocation.lat,
                        formData.pickupLocation.lng,
                      ]}
                      icon={previewIcon}
                    />
                  </MapContainer>
                </div>
                <p className="selected-address">{formData.pickupLocation.address}</p>
              </section>
            ) : null}

            {showLocationPicker ? (
              <LocationPicker
                initialLocation={formData.pickupLocation}
                onConfirm={handleLocationConfirm}
                onCancel={() => setShowLocationPicker(false)}
              />
            ) : null}

            <label className="field">
              <span>Valeye Not</span>
              <textarea
                name="noteToValet"
                value={formData.noteToValet}
                onChange={handleInputChange}
                placeholder="Valeye iletmek istediğiniz not..."
                maxLength={500}
              />
              <small className="character-count">
                {formData.noteToValet.length}/500
              </small>
            </label>

            <button
              type="submit"
              className="primary-button submit-button"
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="button-spinner" />
                  Gönderiliyor...
                </>
              ) : (
                "Arabanı Teslim Al"
              )}
            </button>
          </form>
        </section>
      ) : (
        renderStatusScreen()
      )}
    </main>
  );
}

export default CustomerDelivery;
